import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { Dialog } from "@headlessui/react"
import {
  Plus,
  Trash2,
  X,
  CreditCard,
  Zap,
  Globe,
  FileText,
  Tag,
  DollarSign,
  LucideIcon,
  Code,
  SlidersHorizontal,
  AlertTriangle
} from "lucide-react"
import {
  BillingConfig,
  BillingConfigAddon,
  BillingConfigDetails
} from "../../../../types"
import {
  useUpdateOrCreateBillingConfigMutation,
  useDeleteBillingConfigMutation
} from "../../../../redux/queries"
import { useToast } from "../../../hooks"
import ReactJSONEditor from "../../react-json-editor"

interface Props {
  orgId: string
  config: BillingConfig | null
  onDeleted: () => void
}

const PLAN_LEVELS: BillingConfigDetails["level"][] = [
  "launch",
  "core",
  "growth",
  "custom"
]

const FEATURE_OPTIONS: { key: string; label: string }[] = [
  { key: "dream_rate", label: "Dream Rate" },
  { key: "sms_followup", label: "SMS Followup" }
]

const PREDEFINED_ADDONS: {
  type: string
  label: string
  monthly_price: number
  description: string
  icon: LucideIcon
}[] = [
  {
    type: "itemized_fees",
    label: "Itemized Fees",
    monthly_price: 50,
    description: "Itemized fee breakdown on billing",
    icon: FileText
  },
  {
    type: "landing_page",
    label: "Landing Page",
    monthly_price: 100,
    description: "Custom landing page",
    icon: Globe
  },
  {
    type: "custom_domain",
    label: "Custom Domain",
    monthly_price: 100,
    description: "Custom domain configuration",
    icon: Zap
  },
  {
    type: "discount",
    label: "Discount",
    monthly_price: 50,
    description: "Monthly discount",
    icon: Tag
  }
]

const emptyDetails: BillingConfigDetails = {
  level: "launch",
  primary_email: "",
  start_date: "",
  cancel_on: "",
  addons: [],
  features: [],
  email_cc: []
}

const BillingForm: FC<Props> = ({ orgId, config, onDeleted }) => {
  const { pushSuccessToast, pushErrorToast } = useToast()
  const [updateOrCreate, { isLoading: isSaving }] =
    useUpdateOrCreateBillingConfigMutation()
  const [deleteBillingConfig, { isLoading: isDeleting }] =
    useDeleteBillingConfigMutation()

  const [details, setDetails] = useState<BillingConfigDetails>(emptyDetails)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [editMode, setEditMode] = useState<"form" | "json">("form")

  const isExisting = !!config?.id

  const isPastStartDate = useMemo(() => {
    if (!config?.details.start_date) return false
    const [month, day, year] = config.details.start_date.split("/").map(Number)
    if (!month || !day || !year) return false
    const startDate = new Date(year, month - 1, day)
    return startDate < new Date()
  }, [config?.details.start_date])

  const isPastCancelDate = useMemo(() => {
    if (!config?.details.cancel_on) return false
    const [month, day, year] = config.details.cancel_on.split("/").map(Number)
    if (!month || !day || !year) return false
    const cancelDate = new Date(year, month - 1, day)
    return cancelDate < new Date()
  }, [config?.details.cancel_on])

  useEffect(() => {
    if (config) {
      setDetails({
        level: config.details.level ?? "launch",
        primary_email: config.details.primary_email ?? "",
        start_date: config.details.start_date ?? "",
        cancel_on: config.details.cancel_on ?? "",
        stripe_customer_id: config.details.stripe_customer_id,
        addons: config.details.addons ?? [],
        features: config.details.features ?? [],
        email_cc: config.details.email_cc ?? []
      })
    } else {
      setDetails(emptyDetails)
    }
  }, [config])

  const configSnapshot = useMemo(() => {
    if (!config) return null
    return {
      level: config.details.level ?? "launch",
      primary_email: config.details.primary_email ?? "",
      start_date: config.details.start_date ?? "",
      cancel_on: config.details.cancel_on ?? "",
      stripe_customer_id: config.details.stripe_customer_id,
      addons: config.details.addons ?? [],
      features: config.details.features ?? [],
      email_cc: config.details.email_cc ?? []
    }
  }, [config])

  const isDirty = useMemo(() => {
    if (!configSnapshot) {
      return JSON.stringify(details) !== JSON.stringify(emptyDetails)
    }
    return JSON.stringify(details) !== JSON.stringify(configSnapshot)
  }, [details, configSnapshot])

  const handleSave = useCallback(async () => {
    try {
      const payload: any = {
        org_id: orgId,
        details
      }
      if (config?.id) {
        payload.id = config.id
      }
      await updateOrCreate(payload).unwrap()
      pushSuccessToast(
        isExisting ? "Billing config updated" : "Billing config created"
      )
    } catch {
      pushErrorToast("Failed to save billing config")
    }
  }, [
    orgId,
    details,
    config,
    updateOrCreate,
    isExisting,
    pushSuccessToast,
    pushErrorToast
  ])

  const handleDelete = useCallback(async () => {
    if (!config?.id) return
    try {
      await deleteBillingConfig(config.id).unwrap()
      pushSuccessToast("Billing config deleted")
      setShowDeleteConfirm(false)
      onDeleted()
    } catch {
      pushErrorToast("Failed to delete billing config")
    }
  }, [config, deleteBillingConfig, pushSuccessToast, pushErrorToast, onDeleted])

  const toggleFeature = (featureKey: string) => {
    setDetails(prev => {
      const features = prev.features ?? []
      return {
        ...prev,
        features: features.includes(featureKey)
          ? features.filter(f => f !== featureKey)
          : [...features, featureKey]
      }
    })
  }

  const addPredefinedAddon = (addon: typeof PREDEFINED_ADDONS[number]) => {
    setDetails(prev => ({
      ...prev,
      addons: [
        ...(prev.addons ?? []),
        {
          type: addon.type,
          label: addon.label,
          monthly_price: addon.monthly_price,
          description: addon.description
        }
      ]
    }))
  }

  const addCustomAddon = () => {
    setDetails(prev => ({
      ...prev,
      addons: [
        ...(prev.addons ?? []),
        {
          type: "custom_fee",
          label: "",
          monthly_price: 0,
          description: ""
        }
      ]
    }))
  }

  const updateAddon = (
    index: number,
    field: keyof BillingConfigAddon,
    value: string | number
  ) => {
    setDetails(prev => ({
      ...prev,
      addons: (prev.addons ?? []).map((addon, i) =>
        i === index ? { ...addon, [field]: value } : addon
      )
    }))
  }

  const removeAddon = (index: number) => {
    setDetails(prev => ({
      ...prev,
      addons: (prev.addons ?? []).filter((_, i) => i !== index)
    }))
  }

  return (
    <div className="space-y-8">
      {/* Pending Cancellation Warning */}
      {details.cancel_on && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">
              Pending Cancellation
            </p>
            <p className="text-sm text-amber-700">
              This organization is scheduled to be cancelled on{" "}
              {details.cancel_on}.
            </p>
          </div>
        </div>
      )}

      {/* Mode toggle */}
      <div className="flex items-center gap-1 rounded-lg border border-gray-200 p-1 bg-gray-50 w-fit">
        <button
          type="button"
          onClick={() => setEditMode("form")}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            editMode === "form"
              ? "bg-white text-gray-900 shadow-sm border border-gray-200"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <SlidersHorizontal size={14} />
          Form
        </button>
        <button
          type="button"
          onClick={() => setEditMode("json")}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
            editMode === "json"
              ? "bg-white text-gray-900 shadow-sm border border-gray-200"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <Code size={14} />
          JSON
        </button>
      </div>

      {editMode === "json" ? (
        <ReactJSONEditor
          name="details"
          json={details}
          onChangeJSON={val => setDetails(val as BillingConfigDetails)}
        />
      ) : (
        <>
          {/* Plan Level */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Plan Level
            </h3>
            <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
              {PLAN_LEVELS.map(level => (
                <button
                  key={level}
                  type="button"
                  onClick={() => setDetails(prev => ({ ...prev, level }))}
                  className={`px-4 py-2 text-sm font-medium rounded-md capitalize transition-colors ${
                    details.level === level
                      ? "bg-white text-gray-900 shadow-sm border border-gray-200"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {level}
                </button>
              ))}
            </div>
          </section>

          {/* Primary Email */}
          <section>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Primary Email
            </label>
            <input
              type="email"
              value={details.primary_email}
              onChange={e =>
                setDetails(prev => ({ ...prev, primary_email: e.target.value }))
              }
              placeholder="billing@example.com"
              className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </section>

          {/* Email CC */}
          <section>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Email CC
            </label>
            <div className="space-y-2 max-w-md">
              {(details.email_cc ?? []).map((email, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={e =>
                      setDetails(prev => ({
                        ...prev,
                        email_cc: (prev.email_cc ?? []).map((v, i) =>
                          i === index ? e.target.value : v
                        )
                      }))
                    }
                    placeholder="cc@example.com"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setDetails(prev => ({
                        ...prev,
                        email_cc: (prev.email_cc ?? []).filter(
                          (_, i) => i !== index
                        )
                      }))
                    }
                    className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  setDetails(prev => ({
                    ...prev,
                    email_cc: [...(prev.email_cc ?? []), ""]
                  }))
                }
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-dashed border-gray-300 rounded-lg bg-white hover:bg-gray-50 text-gray-500 transition-colors"
              >
                <Plus size={14} />
                Add CC
              </button>
            </div>
          </section>

          {/* Start Date */}
          <section>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Start Date
            </label>
            {isPastStartDate ? (
              <div className="flex items-center gap-2 max-w-xs px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                {details.start_date}
              </div>
            ) : (
              <input
                type="text"
                value={details.start_date}
                onChange={e =>
                  setDetails(prev => ({ ...prev, start_date: e.target.value }))
                }
                placeholder="MM/DD/YYYY"
                className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            )}
          </section>

          {/* Cancel Date */}
          <section>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Cancel Date
            </label>
            {isPastCancelDate ? (
              <div className="flex items-center gap-2 max-w-xs px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                {details.cancel_on}
              </div>
            ) : (
              <input
                type="text"
                value={details.cancel_on ?? ""}
                onChange={e =>
                  setDetails(prev => ({ ...prev, cancel_on: e.target.value }))
                }
                placeholder="MM/DD/YYYY"
                className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            )}
          </section>

          {/* Stripe Customer ID */}
          {details.stripe_customer_id && (
            <section>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Stripe Customer ID
              </label>
              <div className="flex items-center gap-2 max-w-md px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                <CreditCard size={16} className="text-gray-400 flex-shrink-0" />
                <span className="font-mono">{details.stripe_customer_id}</span>
              </div>
            </section>
          )}

          {/* Add-ons */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Add-ons
            </h3>

            {/* Quick-add buttons */}
            <div className="flex flex-wrap gap-2 mb-4">
              {PREDEFINED_ADDONS.map(addon => {
                const Icon = addon.icon
                return (
                  <button
                    key={addon.type}
                    type="button"
                    onClick={() => addPredefinedAddon(addon)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg bg-white hover:bg-gray-50 text-gray-700 transition-colors"
                  >
                    <Icon size={14} />
                    {addon.label}
                    <span className="text-gray-400">
                      ${addon.monthly_price}/mo
                    </span>
                  </button>
                )
              })}
              <button
                type="button"
                onClick={addCustomAddon}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-dashed border-gray-300 rounded-lg bg-white hover:bg-gray-50 text-gray-500 transition-colors"
              >
                <Plus size={14} />
                Custom Fee
              </button>
            </div>

            {/* Addon rows */}
            {(details.addons ?? []).length > 0 && (
              <div className="space-y-3">
                {(details.addons ?? []).map((addon, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg bg-white"
                  >
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Label
                        </label>
                        <input
                          type="text"
                          value={addon.label}
                          onChange={e =>
                            updateAddon(index, "label", e.target.value)
                          }
                          placeholder="Add-on label"
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Price/mo
                        </label>
                        <div className="relative">
                          <DollarSign
                            size={14}
                            className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
                          />
                          <input
                            type="number"
                            value={addon.monthly_price}
                            onChange={e =>
                              updateAddon(
                                index,
                                "monthly_price",
                                parseFloat(e.target.value) || 0
                              )
                            }
                            className="w-full pl-7 pr-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Description
                        </label>
                        <input
                          type="text"
                          value={addon.description}
                          onChange={e =>
                            updateAddon(index, "description", e.target.value)
                          }
                          placeholder="Description"
                          className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAddon(index)}
                      className="mt-5 p-1 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Features */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Features
            </h3>
            <div className="flex flex-wrap gap-4">
              {FEATURE_OPTIONS.map(feature => {
                const isActive =
                  details.features?.includes(feature.key) ?? false
                return (
                  <label
                    key={feature.key}
                    className="relative inline-flex items-center cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={isActive}
                      onChange={() => toggleFeature(feature.key)}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 peer-focus:ring-2 peer-focus:ring-blue-300 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full" />
                    <span className="ml-2 text-sm text-gray-700">
                      {feature.label}
                    </span>
                  </label>
                )
              })}
            </div>
          </section>
        </>
      )}

      {/* Actions */}
      <section className="flex items-center gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={handleSave}
          disabled={!isDirty || isSaving}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          {isSaving ? "Saving..." : "Save"}
        </button>
        {isExisting && (
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isDeleting}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
          >
            <Trash2 size={14} />
            Delete
          </button>
        )}
      </section>

      {/* Delete confirmation */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-sm rounded-lg bg-white p-6 shadow-xl">
            <Dialog.Title className="text-lg font-bold text-gray-900 mb-2">
              Delete Billing Config
            </Dialog.Title>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this billing configuration? This
              action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400"
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  )
}

export default BillingForm
